import { useTranslation } from 'react-i18next';
import { cn } from '@parrot-software/utils';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { displayValue, formatBrand } from '@subscriptions/presentation/utils';

interface CardPaymentMethodDetailProps {
  paymentMethod: SubscriptionBlockViewDto['paymentMethod'];
  className?: string;
}

/**
 * CardPaymentMethodDetail - Boxed card payment method summary shared by the
 * block detail and the subscriptions list.
 *
 * Renders "Tarjeta: <Brand> **** <last4>" — the brand casing is normalized via
 * `formatBrand`, falling back to the generic "Tarjeta" label when brand or
 * last-four digits are missing — plus a "Vencimiento: MM/AA" row when the
 * payment method exposes an expiration date.
 *
 * @param {Object} props - Component properties
 * @param {string} [props.className] - Extra classes merged into the bordered box
 * @param {SubscriptionBlockViewDto['paymentMethod']} props.paymentMethod - Payment method data
 * @returns {React.ReactElement} Card payment method detail box
 *
 * @example
 * <CardPaymentMethodDetail paymentMethod={block.paymentMethod} />
 */
export const CardPaymentMethodDetail = ({ className, paymentMethod }: CardPaymentMethodDetailProps) => {
  const { t } = useTranslation('subscriptions');

  const cardLabel =
    paymentMethod?.brand && paymentMethod?.lastFourDigits
      ? t('paymentMethod.cardLabel', {
          brand: formatBrand(paymentMethod.brand),
          lastFourDigits: paymentMethod.lastFourDigits,
        })
      : t('paymentMethod.card');

  return (
    <div
      className={cn('flex flex-col gap-1.5 rounded-lg border border-border p-3 font-sans text-sm leading-5', className)}
    >
      <div className="flex gap-2">
        <span className="text-muted-foreground">{t('paymentMethod.cardRowLabel')}</span>
        <span className="font-semibold text-foreground">{cardLabel}</span>
      </div>
      {paymentMethod?.expirationDate && (
        <div className="flex gap-1">
          <span className="text-muted-foreground">{t('paymentMethod.expiration')}</span>
          <span className="font-semibold text-foreground">{displayValue(paymentMethod.expirationDate)}</span>
        </div>
      )}
    </div>
  );
};
